import { useState, useRef, useEffect } from 'react';
import cx from 'classnames';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import config from '@plone/registry';
import styles from '@redturtle/qubicaamf-theme/components/Blocks/Listing/DiveInTheSector/styles.module.css';
import type {
  BaseLink,
  Slide as BaseSlide,
} from '@redturtle/qubicaamf-theme/components/Blocks/ImageGallery/Slides';

export interface DITSLink extends BaseLink {
  dits_short_title?: string | null;
  dits_card_title_color?: string | null;
  testimonials_macro_type_info?: {
    description?: string;
    title: string;
    uid: string;
    url: string;
  };
  news_macro_type_info?: {
    description?: string;
    title: string;
    uid: string;
    url: string;
  };
}

type Slide = Omit<BaseSlide, 'description' | 'linkMore' | 'link'> & {
  link: DITSLink;
};

interface SlidesProps {
  slides: Slide[];
  editMode: boolean;
}

const Slides = (props: SlidesProps) => {
  const { slides, editMode = false } = props;
  const Image = config.getComponent('Image').component;
  const [isScrolling, setIsScrolling] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check if the container is scrollable
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const hasOverflow = container.scrollWidth > container.clientWidth;
      setIsScrollable(hasOverflow);
    }
  }, [slides]);

  const handleScroll = () => {
    // Show the scrollbar thumb
    setIsScrolling(true);

    // Clear any previous timeout
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // Hide the scrollbar thumb after 1 second of no scrolling
    scrollTimeout.current = setTimeout(() => {
      setIsScrolling(false);
    }, 100); // Adjust the delay as needed
  };

  return (
    <div
      ref={containerRef}
      className={cx('block-dits-slides', styles.slides, {
        [styles.scrollbar]: isScrolling,
        [styles.justifyCenter]: !isScrollable,
      })}
      onScroll={handleScroll}
    >
      {slides.map((slide) => {
        const title = slide.link?.dits_short_title || slide.title;
        return (
          <div className={styles.slide} key={slide['@id']}>
            {slide.link ? (
              slide.image?.image_scales?.preview_image ? (
                <UniversalLink
                  item={slide.link}
                  href={slide.link['@id']}
                  title={title || 'link'}
                  openLinkInNewTab={true}
                  className={cx(styles.slideLink, {
                    [styles.white]:
                      slide.link.dits_card_title_color === 'white',
                  })}
                  onClick={(e: React.SyntheticEvent<HTMLLinkElement>) => {
                    if (editMode) e.preventDefault();
                  }}
                >
                  <div className={styles.slideBody}>
                    <div className={styles.slideSubject}>
                      {slide.link?.testimonials_macro_type_info?.title ??
                        slide.link?.news_macro_type_info?.title}
                    </div>

                    <h3 className={styles.slideTitle}>{title}</h3>
                  </div>
                  <Image
                    alt={title ?? ''}
                    item={slide.image}
                    className={styles.slideImg}
                    sizes="(min-width: 576px) 540px, (min-width: 768px) 668px, (min-width: 992px) 960px, (min-width: 1200px) 1184px, (min-width: 1400px) 1320px"
                  />
                </UniversalLink>
              ) : (
                <UniversalLink
                  item={slide.link}
                  href={slide.link['@id']}
                  title={title || 'link'}
                  openLinkInNewTab={true}
                  className={styles.slideLink}
                  onClick={(e: React.SyntheticEvent<HTMLLinkElement>) => {
                    if (editMode) e.preventDefault();
                  }}
                >
                  <div className={styles.noImg}>
                    <div
                      className={cx(styles.slideBody, {
                        [styles.white]:
                          slide.link.dits_card_title_color === 'white',
                      })}
                    >
                      <div className={styles.slideSubject}>
                        {slide.link?.testimonials_macro_type_info?.title ??
                          slide.link?.news_macro_type_info?.title}
                      </div>

                      <h3 className={styles.slideTitle}>{title}</h3>
                    </div>
                  </div>
                </UniversalLink>
              )
            ) : (
              <Image
                alt={title ?? ''}
                item={slide.image}
                className={styles.slideImg}
                sizes="(min-width: 576px) 540px, (min-width: 768px) 668px, (min-width: 992px) 960px, (min-width: 1200px) 1184px, (min-width: 1400px) 1320px"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Slides;
